import React from "react";
import { forwardRef, useImperativeHandle, useState } from "react";
import PropTypes from "prop-types";
import { Grid, InputLabel, MenuItem, Select } from "@material-ui/core";
import OutlinedTextField from '../../OutlinedTextField';

import translate from "../../../locales";
import PeriodsTableDialog from "../../Dialogs/PeriodsTableDialog";
import { bucketTypes, countDecimalPlaces, limitNumber } from "../../../_constants/emparkConstants";
import { toDateObjectFromJson, toDateString } from "../../../_helpers/dateConvert";

const __t = translate;

const RuleMappingForm = forwardRef((props, ref) => {
    console.log('RuleMappingForm props.ruleMapping', props.ruleMapping);
    const [name, setName] = useState(props.ruleMapping ? props.ruleMapping.name : "");
    const [nameError, setNameError] = useState(null);
    const [description, setDescription] = useState(props.ruleMapping ? props.ruleMapping.description : "");
    const [bucketForcedRotationId, setBucketForcedRotationId] = useState(props.ruleMapping && props.ruleMapping.bucketForcedRotation ? props.ruleMapping.bucketForcedRotation.id : "");
    const [bucketFreePeriodId, setBucketFreePeriodId] = useState(props.ruleMapping && props.ruleMapping.bucketFreePeriod ? props.ruleMapping.bucketFreePeriod.id : "");
    const [multiplier, setMultiplier] = useState(props.ruleMapping ? { value: props.ruleMapping.multiplier === null ? '' : String(props.ruleMapping.multiplier), step: props.ruleMapping.step, roundingType: props.ruleMapping.roundingType } : { value: '', step: '', roundingType: '' });
    const [multiplierError, setMultiplierError] = useState(null);
    const [periods, setPeriods] = useState(props.ruleMapping ?
        props.ruleMapping.periods.map(p => ({ fromDate: toDateObjectFromJson(p.fromDate), toDate: toDateObjectFromJson(p.toDate) }))
        :
        []
    );
    const [periodsError, setPeriodsError] = useState(false);

    const [periodModal, setPeriodModal] = useState(false);

    useImperativeHandle(ref, () => ({
        getData: () => {
            let hasError = false;

            setNameError(null);
            setPeriodsError(false);
            setMultiplierError(null);

            if (!name || name.trim() === '') {
                setNameError(__t('name_required'));
                hasError = true;
            }

            if (!periods || periods.length === 0) {
                setPeriodsError(true);
                hasError = true;
            }

            if (multiplier.step || multiplier.roundingType) {
                if (!multiplier.value || !multiplier.step || !multiplier.roundingType) {
                    setMultiplierError(__t('error_rule_multiplier'));
                    hasError = true;
                }
            }

            if (hasError)
                return false;

            return {
                name: name,
                description: description,
                bucketForcedRotationId: bucketForcedRotationId,
                bucketFreePeriodId: bucketFreePeriodId,
                multiplier: multiplier,
                periods: periods
            };
        }
    }))

    return (
        <>
            <PeriodsTableDialog
                open={periodModal}
                periods={periods}
                onCancel={() => setPeriodModal(false)}
                onSubmit={(selectedPeriods) => {
                    setPeriods(selectedPeriods);
                    setPeriodModal(false);
                }}
            />
            <Grid container spacing={8} direction="row" justify="flex-start" alignItems="center">
                <Grid item xs={2}>
                    <InputLabel shrink required>{__t('name')}:</InputLabel>
                </Grid>
                <Grid item xs={4}>
                    <OutlinedTextField
                        value={name}
                        error={nameError}
                        onChange={(e) => setName(e.target.value.replace(" ", "_"))}
                        inputProps={{ maxLength: 70, style: { textTransform: 'uppercase' } }}
                        disabled={props.readonly}
                    />
                </Grid>
                <Grid item xs={6} />
                <Grid item xs={2}>
                    <InputLabel shrink >{__t('description')}:</InputLabel>
                </Grid>
                <Grid item xs={10}>
                    <OutlinedTextField
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        inputProps={{ style: { textTransform: 'uppercase' } }}
                        disabled={props.readonly}
                    />
                </Grid>
                <Grid item xs={2}>
                    <InputLabel shrink >{__t('periods')}:</InputLabel>
                </Grid>
                <Grid item xs={10}>
                    <OutlinedTextField
                        value={periods.map(p => `[${toDateString(p.fromDate)} - ${toDateString(p.toDate)}]`).join(' ')}
                        error={periodsError}
                        onClick={() => {
                            setPeriodModal(true);
                        }}
                        inputProps={{ style: { textTransform: 'uppercase' }, readOnly: true }}
                        disabled={props.readonly}
                    />
                </Grid>
                {props.readonly && !bucketForcedRotationId ?
                    <Grid item xs={6} />
                    : <>
                        <Grid item xs={2}>
                            <InputLabel shrink>{__t('bucket')} {__t('forced_rotation')}:</InputLabel>
                        </Grid>
                        <Grid item xs={4}>
                            <Select
                                value={bucketForcedRotationId}
                                style={{ width: '100%' }}
                                onChange={(e) => setBucketForcedRotationId(e.target.value)}
                                disabled={props.readonly}
                            >
                                <MenuItem value="">{__t('no_value')}</MenuItem>
                                {props.buckets.filter(b => b.bucketType === bucketTypes.FORCED_ROTATION).map(bucket =>
                                    <MenuItem key={bucket.id} value={bucket.id}>{bucket.name}</MenuItem>
                                )}
                            </Select>
                        </Grid>
                    </>
                }
                {props.readonly && !bucketFreePeriodId ?
                    <Grid item xs={6} />
                    : <>
                        <Grid item xs={2} style={{ textAlign: 'right' }}>
                            <InputLabel shrink>{__t('bucket')} {__t('free_periods')}:</InputLabel>
                        </Grid>
                        <Grid item xs={4}>
                            <Select
                                value={bucketFreePeriodId}
                                style={{ width: '100%' }}
                                onChange={(e) => setBucketFreePeriodId(e.target.value)}
                                disabled={props.readonly}
                            >
                                <MenuItem value="">{__t('no_value')}</MenuItem>
                                {props.buckets.filter(b => b.bucketType === bucketTypes.FREE_PERIODS).map(bucket =>
                                    <MenuItem key={bucket.id} value={bucket.id}>{bucket.name}</MenuItem>
                                )}
                            </Select>
                        </Grid>
                    </>
                }
                {props.readonly && multiplier.value === '' ?
                    null
                    :
                    <>
                        <Grid item xs={2}>
                            <InputLabel shrink>{__t('multiplier')}:</InputLabel>
                        </Grid>
                        <Grid item xs={2}>
                            <OutlinedTextField
                                value={multiplier.value}
                                type="number"
                                onChange={(e) => {
                                    if (e.target.value === '') {
                                        setMultiplier({ ...multiplier, value: '' });
                                        return;
                                    }

                                    const n = Number(e.target.value);

                                    if (!limitNumber(e.target.value, 0, 10) || countDecimalPlaces(e.target.value) > 2) {
                                        return;
                                    }

                                    setMultiplier({ ...multiplier, value: e.target.value })
                                }}
                                disabled={props.readonly}
                            />
                        </Grid>
                        <Grid item xs={2} style={{ textAlign: 'right' }}>
                            <InputLabel shrink>{__t('step')}:</InputLabel>
                        </Grid>
                        <Grid item xs={2}>
                            <Select
                                value={multiplier.step}
                                style={{ width: '100%' }}
                                onChange={(e) => setMultiplier({ ...multiplier, step: e.target.value })}
                                disabled={props.readonly}
                            >
                                <MenuItem value="">{__t('no_value')}</MenuItem>
                                <MenuItem value={0.01}>0,01</MenuItem>
                                <MenuItem value={0.05}>0,05</MenuItem>
                                <MenuItem value={0.10}>0,10</MenuItem>
                            </Select>
                        </Grid>
                        <Grid item xs={2} style={{ textAlign: 'right' }}>
                            <InputLabel shrink>{__t('rounding')}:</InputLabel>
                        </Grid>
                        <Grid item xs={2}>
                            <Select
                                value={multiplier.roundingType}
                                style={{ width: '100%' }}
                                onChange={(e) => setMultiplier({ ...multiplier, roundingType: e.target.value })}
                                disabled={props.readonly}
                            >
                                <MenuItem value="">{__t('no_value')}</MenuItem>
                                <MenuItem value={"DOWN"}>{__t('down')}</MenuItem>
                                <MenuItem value={"UP"}>{__t('up')}</MenuItem>
                            </Select>
                        </Grid>
                    </>
                }
            </Grid>
            <div style={{ textAlign: "center", color: "red", marginTop: multiplierError ? 5 : 0 }}>{multiplierError}</div>
        </>
    )
});

RuleMappingForm.propTypes = {
    ruleMapping: PropTypes.object,
    readonly: PropTypes.bool,
}

export default RuleMappingForm;